import { CoreApi, Product, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
import { BestaOpeningFunctionComponent } from '@inter-ikea-storageone/range-besta';
import { ComponentIds, Entity, HierarchyUtils } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { hasOpeningFunction } from 'rangeConfig/shared/utils/frontLayouts';
import { getAllFrameGroupsForTops, getTotalWidth } from '../utils/topPanels';

export const matchInteriorsToFrames = (products: Product[], entitiesInScene: Entity[]) => {
  const irrelevantFrameTypes = [ProductType.EKET_FRAME, ProductType.EKET_FRAME_WITH_GLASS_DOOR_ASM];

  const frames = entitiesInScene.filter(entity => {
    const productType = entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
      ?.productType;
    return (
      entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
      productType &&
      !irrelevantFrameTypes.includes(productType)
    );
  });
  if (frames.length === 0) {
    return products;
  }
  const frameDepths = frames
    .map(
      frame =>
        frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size.depth
    )
    .filter((depth): depth is number => depth !== undefined);

  return products
    .map(product => {
      const interiorDepth = product.communicatedDimensions.depth;
      return frameDepths.includes(interiorDepth) ? product : null;
    })
    .filter((p): p is Product => p !== null);
};

export const matchDoorsToFrames = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = entitiesInScene.filter(entity =>
    entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
  );
  if (frames.length === 0) {
    return products;
  }

  const frameHeights = frames
    .map(
      frame =>
        frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size.height
    )
    .filter((height): height is number => height !== undefined);

  return products
    .map(product => {
      const doorHeight = product.communicatedDimensions.height;
      return frameHeights.find(height => height >= doorHeight) ? product : null;
    })
    .filter((p): p is Product => p !== null);
};

export const matchDoorStyleToFrames = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = entitiesInScene.filter(entity =>
    entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
  );
  if (frames.length === 0) {
    return products;
  }
  const framesWithoutFronts = frames.filter(
    frame =>
      HierarchyUtils.descendants(frame).filter(
        child =>
          child.hasComponent(ComponentIds.DOOR_COMPONENT_ID) ||
          child.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)
      ).length === 0
  );
  if (framesWithoutFronts.length > 0) {
    // Return all products since we don't know which fronts are included in frames default layout
    return products;
  }
  const frontsInScene = frames.flatMap(frame =>
    HierarchyUtils.descendants(frame).filter(
      child =>
        child.hasComponent(ComponentIds.DOOR_COMPONENT_ID) ||
        child.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)
    )
  );
  const frontTypesOnFrame = frontsInScene
    .map(front => front.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType)
    .filter((type): type is ProductType => type !== undefined);
  return products.filter(product => frontTypesOnFrame.includes(product.type));
};

export const matchDrawerOpeningToFrames = (products: Product[], entitiesInScene: Entity[]) => {
  const pushOpenDrawerProducts = products.filter(product =>
    hasOpeningFunction(product, ProductType.BESTA_PUSH_OPEN)
  );
  const softClosingDrawerProducts = products.filter(product =>
    hasOpeningFunction(product, ProductType.BESTA_SOFT_CLOSING)
  );
  const doorProducts = products.reduce((acc: Product[], curr: Product) => {
    if (
      curr.type === ProductType.BESTA_DOOR_38_ASM ||
      curr.type === ProductType.BESTA_DOOR_64_ASM ||
      curr.type === ProductType.BESTA_DOOR_192_ASM
    ) {
      return [...acc, curr];
    }
    return acc;
  }, []);

  const frames = entitiesInScene.filter(entity =>
    entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
  );
  if (frames.length === 0) {
    return [...pushOpenDrawerProducts, ...doorProducts];
  }

  const inScenePushOpenDrawers = entitiesInScene.filter(
    entity =>
      entity.getComponent<BestaOpeningFunctionComponent>(BestaOpeningFunctionComponent.id)
        ?.openingFunction === ProductType.BESTA_PUSH_OPEN
  );
  const inSceneSoftClosingDrawers = entitiesInScene.filter(
    entity =>
      entity.getComponent<BestaOpeningFunctionComponent>(BestaOpeningFunctionComponent.id)
        ?.openingFunction === ProductType.BESTA_SOFT_CLOSING
  );

  if (inScenePushOpenDrawers.length >= inSceneSoftClosingDrawers.length) {
    return [...pushOpenDrawerProducts, ...doorProducts];
  } else {
    return [...softClosingDrawerProducts, ...doorProducts];
  }
};

/**
 * Check if organizer, normal or rotated, fits frames in scene
 */
export const matchShelfOrganizerToEntitesInScene = (
  products: Product[],
  entitiesInScene: Entity[]
) => {
  const frames = entitiesInScene.filter(entity =>
    entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
  );
  if (frames.length === 0) {
    return products;
  }
  if (
    frames.every(
      frame =>
        frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size.depth === 200
    )
  ) {
    return products.filter(
      product =>
        product.communicatedDimensions.width < 200 || product.communicatedDimensions.depth < 200
    );
  }
  return products;
};

export const matchDrawerOrganizerToEntitesInScene = (
  products: Product[],
  entitiesInScene: Entity[]
) => {
  const frames = entitiesInScene.filter(entity =>
    entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
  );
  if (frames.length === 0) {
    return products;
  }
  const drawersInScene =
    entitiesInScene.filter(entity => entity.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)).length >
    0;

  return products.filter(product =>
    [ProductType.BESTA_DRAWER_ORGANIZER, ProductType.BESTA_SHELF_AND_DRAWER_ORGANIZER].includes(
      product.type
    )
      ? drawersInScene
      : true
  );
};

export const matchTopPanelsToFramesInScene = (
  products: Product[],
  entitiesInScene: Entity[],
  coreApi: CoreApi
) => {
  const tvBenchesInScene = coreApi.entities.getByProductType(ProductType.BESTA_TV_BENCH);
  const regularFrameGroupsInScene = getAllFrameGroupsForTops(coreApi);
  if (tvBenchesInScene.length === 0 && regularFrameGroupsInScene.length === 0) {
    return products;
  }
  const frameGroupWidthsInScene = regularFrameGroupsInScene.map(group =>
    getTotalWidth(group, coreApi)
  );
  return products.filter(product => {
    if (product.type === ProductType.BESTA_TOP_PANEL_TV_BENCH) {
      return tvBenchesInScene.some(
        bench =>
          coreApi.entities.getEntityComSize(bench)?.width === product.communicatedDimensions.width
      );
    } else {
      return frameGroupWidthsInScene.some(width => width >= product.communicatedDimensions.width);
    }
  });
};
